import { LitElement, html, nothing } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { overviewCard, statusBadge } from '@vitesse/base-ui/components/templates';
import { button } from '@vitesse/base-ui/components/base-buttons';
import { t } from '@vitesse/base-ui/i18n';
import { fmtAgo } from '@vitesse/base-ui/format';
import { Dashboards } from '@vitesse/proto/vitesse/v1/dashboard_pb.js';
import type { DashboardSummary } from '../../../core/domain/dashboard';
import type { DashboardManager } from '../../managers/dashboard-manager';

/*
 * <vitesse-dashboard-card> — the ONE dashboard list-card, everywhere a board is
 * listed (home, the gateway page).
 *
 * A self-contained component rather than a template function, and the delete
 * flow is why: a card that can delete owns its own confirm dialog, its own
 * in-flight state, and its own error — the controller and integration cards
 * both settled on this shape, and a third entity doing it via callbacks threaded
 * from every page would be the drift the one-card rule exists to prevent. (It
 * BEGAN as a template function, extracted when the gateway page shipped a
 * hardcoded count; it became a component the moment it needed a dialog.)
 *
 * Duplicate lives here too, and is deliberately CLIENT-side create: create is
 * the one metered verb, so a copy passes the same ceiling as any new board and
 * a refusal reads the same sentence.
 *
 * The description is the BOARD'S OWN, never its type: a description slot
 * filled with the word "Dashboard" occupies the one line somebody scans to
 * decide which of nine boards they want, and says nothing.
 */
@customElement('vitesse-dashboard-card')
export class DashboardCard extends LitElement {
  @property({ attribute: false }) summary?: DashboardSummary;
  @property({ attribute: false }) manager?: DashboardManager;
  @property() gatewayId = '';
  /** Friendly gateway label — the flat home views set it (which box?); the
   *  gateway page omits it (every card there is that box's already). */
  @property() gatewayName = '';
  /** The owning gateway's live connection state — drives the card's live badge
   *  (green Live / yellow connecting / gray offline), re-rendered as the
   *  roster stream flips it. '' = the page didn't say (no badge). */
  @property() connState = '';
  @state() private confirmOpen = false;
  @state() private busy = false;
  @state() private deleteError = '';

  protected createRenderRoot(): HTMLElement {
    return this;
  }

  private nav(): void {
    if (!this.summary) return;
    this.dispatchEvent(
      new CustomEvent('navigate', {
        detail: { view: 'dashboard', gatewayId: this.gatewayId, dashboardId: this.summary.id },
        bubbles: true,
        composed: true,
      }),
    );
  }

  private async confirmDelete(): Promise<void> {
    if (!this.summary || !this.manager || this.busy) return;
    this.busy = true;
    this.deleteError = '';
    try {
      const r = await this.manager.delete(this.summary.id);
      if (r.ok) this.confirmOpen = false; // the card drops via the roster stream
      else this.deleteError = r.error || t('dashboards.deleteFailed');
    } catch (err) {
      this.deleteError = err instanceof Error ? err.message : String(err);
    } finally {
      this.busy = false;
    }
  }

  render() {
    const d = this.summary;
    if (!d) return nothing;
    // The wrapping span stops action clicks bubbling to the card, which would
    // navigate into the board instead.
    const actions = this.manager
      ? html`<span @click=${(e: Event) => e.stopPropagation()}
          ><vitesse-gated .rpc=${Dashboards.method.delete} inline
            >${button({ label: t('dashboards.delete'), title: t('dashboards.delete'), icon: 'trash-2', color: 'red', size: 'sm', onClick: () => (this.confirmOpen = true) })}</vitesse-gated
          ></span
        >`
      : nothing;
    return html`${overviewCard({
        title: d.title,
        entityType: 'dashboard',
        actions,
        description: d.description || t('dashboards.descriptionFallbackShort'),
        clickable: true,
        onClick: () => this.nav(),
        onHover: () => this.manager?.prefetchBody(d.id),
        badges: html`${
          this.connState === 'connected'
            ? statusBadge('green', t('dashboards.live'))
            : this.connState === 'offline' || this.connState === 'disconnected'
              ? statusBadge('gray', t('status.offline'))
              : this.connState
                ? statusBadge('yellow', this.connState)
                : nothing
        }${this.gatewayName ? statusBadge('gateway', this.gatewayName, 'hard-drive') : nothing}${
          d.updatedAtMs ? statusBadge('gray', t('dashboards.updatedAgo', { ago: fmtAgo(d.updatedAtMs) })) : nothing
        }`,
      })}
      ${this.confirmOpen
        ? html`<vitesse-confirm-dialog
            .open=${true}
            heading=${t('dashboards.deleteHeading')}
            message=${t('dashboards.deleteMessage', { title: d.title })}
            detail=${t('dashboards.deleteDetail')}
            confirm-label=${t('dashboards.delete')}
            danger
            ?busy=${this.busy}
            error=${this.deleteError}
            @confirm=${() => void this.confirmDelete()}
            @dialog-close=${() => (this.confirmOpen = false)}
          ></vitesse-confirm-dialog>`
        : nothing}`;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'vitesse-dashboard-card': DashboardCard;
  }
}
